// SPARK-kontraktene (no.gna.spark.*), speiler docs/SPARK_PAYLOAD.md v1.
//
// Desimaltall kan komme som JSON-tall ELLER Matrix-trygg streng (Synapse
// forbyr float i event-innhold). `num` godtar begge — samme toleranse som
// Rust-parseren (as_f64_flex) og cot_mapping._as_float.
import { z } from 'zod';
/** Tall som enten er JSON-number eller tall-som-streng → number | undefined. */
export const num = z
    .union([z.number(), z.string()])
    .optional()
    .transform((v) => {
    if (v === undefined || v === null)
        return undefined;
    // Trim strenger; ren whitespace («   ») → undefined (ikke 0, som Number gir).
    const s = typeof v === 'string' ? v.trim() : v;
    if (s === '')
        return undefined;
    const n = typeof s === 'number' ? s : Number(s);
    return Number.isFinite(n) ? n : undefined;
});
/** Deteksjonstype — utvidbar (ukjent verdi tolereres, faller til 'unknown'). */
export const DetectionType = z
    .enum(['drone', 'gunshot', 'artillery', 'vehicle', 'person', 'em'])
    .catch('drone');
/** Løst kildepunkt (modus B, multilaterasjon). */
export const TargetSchema = z.object({
    lat: num,
    lon: num,
    cep_m: num,
});
export const DetectionSchema = z.object({
    type: z.string().optional(),
    /** Deteksjonsmetode (#38–41): rf_2_4ghz | acoustic | accelerometer | … */
    method: z.string().optional(),
    /** Egen hendelse (eget skudd) vs. ekstern. */
    own: z.boolean().optional(),
    confidence: num,
    bearing_deg: num,
    range_m: num,
    target: TargetSchema.optional(),
    count: z.union([z.number(), z.string()]).optional().transform((v) => {
        if (v === undefined || v === '')
            return undefined;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isInteger(n) ? n : undefined;
    }),
    note: z.string().optional(),
});
export const PosSchema = z.object({
    lat: num,
    lon: num,
    alt_m: num,
    acc_m: num,
});
/** Mesh-helse per node (#37/#66): signal + topologi. Alle valgfrie. */
export const MeshSchema = z.object({
    rssi_dbm: num,
    snr_db: num,
    neighbors: z.union([z.number(), z.string()]).optional().transform((v) => {
        if (v === undefined || v === '')
            return undefined;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isInteger(n) ? n : undefined;
    }),
    hops: z.union([z.number(), z.string()]).optional().transform((v) => {
        if (v === undefined || v === '')
            return undefined;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isInteger(n) ? n : undefined;
    }),
    /** Faktiske nabo-node-id-er (til topologi-graf). Valgfritt/bakoverkompatibelt;
     *  `neighbors` (antall) beholdes når noden bare rapporterer et tall. */
    neighbor_ids: z.array(z.string()).optional(),
});
/** Org-tilhørighet for en node/hendelse (RBAC-scope). Settes ved autorisering
 * av SPARK-noden (lagfører) eller av relayet. Alle felt valgfrie. */
export const OrgSchema = z.object({
    district: z.string().optional(),
    area: z.string().optional(),
    troop: z.string().optional(),
    team: z.string().optional(),
});
/** no.gna.spark.sensor v1 — én måling/hendelse. */
export const SensorEventSchema = z.object({
    v: z.number().optional(),
    node_id: z.string(),
    /** Org-tilhørighet (RBAC-scope), om kjent. */
    org: OrgSchema.optional(),
    seq: z.union([z.number(), z.string()]).optional(),
    ts: z.union([z.number(), z.string()]).optional().transform((v) => {
        if (v === undefined || v === '')
            return undefined;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) ? n : undefined;
    }),
    kind: z.enum(['position', 'detection', 'telemetry', 'alarm', 'status']).catch('telemetry'),
    pos: PosSchema.optional(),
    batt_pct: num,
    mesh: MeshSchema.optional(),
    detection: DetectionSchema.optional(),
    extra: z.record(z.unknown()).optional(),
});
/** no.gna.spark.node v1 — enhetsregister. */
export const SensorNodeSchema = z.object({
    v: z.number().optional(),
    node_id: z.string(),
    name: z.string().optional(),
    hw: z.string().optional(),
    fw: z.string().optional(),
    unit: z.string().optional(),
    sensors: z.array(z.string()).optional(),
    decommissioned: z.boolean().optional(),
});
/** no.gna.spark.control v1 — ønsket funksjonskonfig (state-event). */
export const SparkFeaturesSchema = z.object({
    broadband: z.boolean().default(false),
    mesh: z.boolean().default(false),
    tracker: z.boolean().default(false),
    cuas_rf: z.boolean().default(false),
    cuas_acoustic: z.boolean().default(false),
    shot_self: z.boolean().default(false),
    shot_acoustic: z.boolean().default(false),
});
/** Driftsmodus (OTA-styrt fra GNÅ/SKADE). Styrer strøm/EMCON/oppløsning. */
export const SparkMode = z.enum([
    'normal', // standard drift
    'lav-effekt', // batterisparing: lengre intervall, mindre radio
    'hoy-ytelse', // tett sampling/rapportering (kort utholdenhet)
    'stealth', // LPI/EMCON: minimér utsending, buffer lokalt
    'dvale', // sover, kun oppvekking på trigger/plan
]);
/** OTA-innstillinger en node kan få dyttet fra GNÅ/SKADE (samme kontrollkanal).
 * Alle felt valgfrie → delta-oppdatering (bare det som settes, endres). */
export const SparkSettingsSchema = z.object({
    mode: SparkMode.optional(),
    /** Samplingsintervall (sekunder mellom målinger). */
    sample_interval_s: z.number().int().min(1).max(86400).optional(),
    /** Rapporteringsintervall (sekunder mellom opplastinger til C2). */
    report_interval_s: z.number().int().min(1).max(86400).optional(),
    /** Mobil-APN (mykt konfigurerbart, ikke hardkodet — bytte 1nce↔Forsvarets eSIM). */
    apn: z.string().max(64).regex(/^[A-Za-z0-9._-]*$/, 'ugyldig APN').optional(),
});
export const SparkControlSchema = z.object({
    v: z.number().optional(),
    node_id: z.string(),
    authorized: z.boolean().default(false),
    team: z.string().optional(),
    features: SparkFeaturesSchema.optional(),
    /** OTA-innstillinger (modus/sampling/rapport/APN). Delta: udefinert = uendret. */
    settings: SparkSettingsSchema.optional(),
    issued_by: z.string().optional(),
    ts: z.union([z.number(), z.string()]).optional(),
});
/** Parse en enkelt SPARK-kontroll-melding (OTA). Null ved ugyldig (aldri kast). */
export function parseSparkControl(raw) {
    const p = SparkControlSchema.safeParse(raw);
    return p.success ? p.data : null;
}
/** Slå sammen en delta-innstilling inn i gjeldende (kun definerte felt endres).
 * Ren funksjon — brukes både på node (anvende) og i C2 (vise ønsket tilstand). */
export function mergeSparkSettings(current, delta) {
    return {
        mode: delta.mode ?? current.mode,
        sample_interval_s: delta.sample_interval_s ?? current.sample_interval_s,
        report_interval_s: delta.report_interval_s ?? current.report_interval_s,
        apn: delta.apn ?? current.apn,
    };
}
/** Maks events prosessert per batch (mot DoS via gigantisk array). */
export const MAX_BATCH = 5000;
/** Batch-form: array, {events:[…]}, {batch:[…]} eller {readings:[…]}. */
export function parseSensorBatch(raw) {
    const pick = (v) => {
        if (Array.isArray(v))
            return v;
        if (v && typeof v === 'object') {
            const o = v;
            for (const key of ['events', 'batch', 'readings']) {
                if (Array.isArray(o[key]))
                    return o[key];
            }
            return [v];
        }
        return [];
    };
    const out = [];
    // Cap antall elementer — beskytter mot gigantisk array (utfyller HTTP-body-
    // grensen i hubben).
    for (const item of pick(raw).slice(0, MAX_BATCH)) {
        const r = SensorEventSchema.safeParse(item);
        if (r.success)
            out.push(r.data);
    }
    return out;
}
//# sourceMappingURL=spark.js.map